import type { ReactNode } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, LogOut } from 'lucide-react';

// The platform-standard identity widget: a circular avatar (initials)
// with a dropdown holding the signed-in user's name/email, any
// app-specific actions (e.g. "Submit feedback"), and "Sign out".
//
// This is the canonical top-bar user menu for EVERY authenticated app —
// PMA, admin, home, accounting, training, and anything built after them.
// It originated as PMA's inline TopBar dropdown and was lifted here so
// the look + interaction converge. Apps own their own header layout
// (brand, nav, notifications); they hand the identity slot to this.
//
// Presentational only: it takes already-resolved strings + callbacks and
// keeps no auth/query state of its own, so each app wires its own store
// and submit logic without this package depending on either.

export interface UserMenuItem {
    /** Visible label, e.g. "Submit feedback". */
    label: string;
    /** Optional leading icon (a lucide icon element). */
    icon?: ReactNode;
    /** Fired on click / Enter / Space (Radix `onSelect`). */
    onSelect: () => void;
}

export interface UserMenuProps {
    /** Full display name. Shown in the trigger and the dropdown header.
     *  Falls back to a generic label when the profile hasn't loaded. */
    name?: string | null;
    /** Secondary trigger line — role name, practice, etc. */
    secondary?: string | null;
    /** Email shown in the dropdown header. Header is hidden when absent. */
    email?: string | null;
    /** Actions rendered above "Sign out" (most apps pass "Submit feedback"). */
    items?: UserMenuItem[];
    /** Sign-out handler. Always rendered as the final item. */
    onSignOut: () => void;
    /** Override the sign-out label (default "Sign out" — platform copy
     *  standard, never "Log out"). */
    signOutLabel?: string;
}

// Initials from the first letters of the first two name words. Empty
// name → "?" so the avatar never renders blank in a half-loaded session
// (token present, /api/me not yet returned).
function initialsFrom(name?: string | null): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
    return (first + last).toUpperCase() || '?';
}

const itemClass =
    'flex items-center gap-2 px-3 py-2 text-sm text-text hover:bg-surface-muted cursor-pointer outline-none';

export function UserMenu({
    name,
    secondary,
    email,
    items = [],
    onSignOut,
    signOutLabel = 'Sign out',
}: UserMenuProps) {
    const displayName = name?.trim() || 'Signed in';

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-surface-muted transition-colors"
                >
                    <div className="w-7 h-7 rounded-full bg-accent text-[#111] flex items-center justify-center text-xs font-bold">
                        {initialsFrom(name)}
                    </div>
                    <div className="hidden sm:block text-left leading-tight">
                        <div className="text-xs font-medium text-text truncate max-w-[140px]">
                            {displayName}
                        </div>
                        {secondary && (
                            <div className="text-[11px] text-text-subtle truncate max-w-[140px]">
                                {secondary}
                            </div>
                        )}
                    </div>
                    <ChevronDown className="w-3.5 h-3.5 text-text-subtle" aria-hidden />
                </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="end"
                    sideOffset={6}
                    className="min-w-[200px] bg-surface border border-border rounded-md shadow-md py-1 z-50"
                >
                    {email && (
                        <div className="px-3 py-2 border-b border-border">
                            <div className="text-xs font-medium text-text truncate">
                                {displayName}
                            </div>
                            <div className="text-[11px] text-text-subtle truncate">
                                {email}
                            </div>
                        </div>
                    )}
                    {items.map((item) => (
                        <DropdownMenu.Item
                            key={item.label}
                            onSelect={item.onSelect}
                            className={itemClass}
                        >
                            {item.icon}
                            {item.label}
                        </DropdownMenu.Item>
                    ))}
                    <DropdownMenu.Item onSelect={onSignOut} className={itemClass}>
                        <LogOut className="w-4 h-4" aria-hidden />
                        {signOutLabel}
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
